import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { filter, map } from 'rxjs/operators';
import { BackButtonService } from '../back-button.service';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProjectDrillingOptions } from '../customer-project-drilling-options.class';
import { CustomerProjectDrilling } from '../customer-project-drilling.interface';
import { CustomerProjectPhaseActivity } from '../customer-project-phase-activity.interface';
import { CustomerProjectPhase } from '../customer-project-phase.interface';
import { CustomerProject } from '../customer-project.interface';
import { FileItem } from '../document-upload/file-item';
import { StorageName } from '../document-upload/storage-name.enum';
import { PdfReportRequest, PdfreportService } from '../pdfreport.service';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { Functionalities } from '../services/auth/functionalities';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { CustomerService } from '../services/customer/customer.service';
import { ExportService } from '../services/export/export.service';
import { FileService } from '../services/file.service';
import { PageTitleService } from '../services/page-title/page-title.service';
import { Utils } from '../utils.class';
import { CustomerProjectDescriptionDialogComponent } from './customer-project-description-dialog/customer-project-description-dialog.component';
import { CustomerProjectExportDialogComponent } from './customer-project-export-dialog/customer-project-export-dialog.component';
import { CustomerProjectPhaseRepliesComponent } from './customer-project-phase-replies/customer-project-phase-replies.component';

@Component({
    selector: 'app-customer-project-detail',
    templateUrl: './customer-project-detail.component.html',
    styleUrls: ['./customer-project-detail.component.scss'],
    standalone: false,
})
export class CustomerProjectDetailComponent extends CodaltComponent implements OnInit {
    @ViewChild('customerProjectPhaseReplies')
    customerProjectPhaseReplies: CustomerProjectPhaseRepliesComponent;

    generatingReport = false;
    generatingExcel = false;
    project: CustomerProject | CustomerProjectDrilling;
    selectedPhase?: CustomerProjectPhase;
    customer: Customer;
    customerId: number;
    projectId: number;
    form: FormGroup;
    fc: {
        driller;
        drillingType;
        foundationType;
        description;
        worknumber;
        street;
        customer;
        execution_date;
    };
    dropdownOptions;
    counts: {
        name: string;
        count: number;
        colorcode: string;
        id: number;
    }[];
    pendingAccessCount = 0;
    hideSidebar = false;

    zipExporting = false;
    statusIdsToExport = [];

    selectedActivity: CustomerProjectPhaseActivity;

    highlightActivityReplyId: number;

    showDonePhasesControl = new FormControl<boolean>(false, { nonNullable: true });
    showDoneActivitiesControl = new FormControl<boolean>(false, { nonNullable: true });

    showDonePhases = toSignal(this.showDonePhasesControl.valueChanges, {
        initialValue: this.showDonePhasesControl.value,
    });
    showDoneActivities = toSignal(this.showDoneActivitiesControl.valueChanges, {
        initialValue: this.showDoneActivitiesControl.value,
    });

    constructor(
        private customerService: CustomerService,
        private customerProjectService: CustomerProjectService,
        private pdfreportService: PdfreportService,
        private fileService: FileService,
        private confirmDialog: ConfirmDialogService,
        private dialog: MatDialog,
        private exportService: ExportService,
        private route: ActivatedRoute,
        private router: Router,
        private backButtonService: BackButtonService,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    ngOnInit(): void {
        this.pageTitleService.setTitle(this.getPageTitleLabel());
        this.hideSidebar = window.matchMedia('(max-width: 767px)').matches;

        this.watchRouteParams(
            [
                this.RouteParamnames.customerId,
                this.RouteParamnames.projectId,
                this.RouteParamnames.phase,
                this.RouteParamnames.phaseActivity,
                this.RouteParamnames.reply,
            ],
            ({ customerId, projectId, phase, phaseActivity, reply }) => {
                this.customerId = +customerId;
                this.projectId = +projectId;
                this.backButtonService.setBackbuttonAction(() =>
                    this.router.navigate([
                        this.Routenames.customers,
                        this.customerId,
                        this.Utils.isDrillingRoute(this.route)
                            ? this.Routenames.drillingProjects
                            : this.Routenames.projects,
                    ])
                );
                this.customerProjectService
                    .getProjectsWorkareaCount(this.projectId)
                    .subscribe(counts => (this.counts = counts));
                if (AuthorisationService.hasFeature(this.Features.CustomerProjectsPendingAccessCounts)) {
                    this.subscriptions.add(
                        this.customerProjectService
                            .getPendingAccessCounts()
                            .subscribe(counts => (this.pendingAccessCount = counts.per_project[this.projectId] ?? 0))
                    );
                }
                this.subscriptions.add(
                    this.customerService.getSingle(this.customerId).subscribe(customer => {
                        this.customer = customer;
                        this.LocalStorage.setCustomer(customer);

                        this.customerProjectService
                            .getProjectDetail(this.projectId)
                            .pipe(map(p => p.data))
                            .subscribe(
                                project => {
                                    this.pageTitleService.setTitle(this.getPageTitleLabel(), project.name);
                                    // PHP stores an empty extra as [], props on a JS array are dropped by JSON.stringify
                                    project.extra = project.extra && !Array.isArray(project.extra) ? project.extra : {};
                                    if (project.type === 'drillings') {
                                        this.dropdownOptions = CustomerProjectDrillingOptions;
                                        this.project = project as CustomerProjectDrilling;
                                        this.fc = {
                                            driller: new FormControl(
                                                this.project.extra.driller
                                                    ? this.project.extra.driller
                                                    : this.LocalStorage.getUser().name,
                                                Validators.required
                                            ),
                                            drillingType: new FormControl(
                                                this.project.extra.drillingType,
                                                Validators.required
                                            ),
                                            foundationType: new FormControl(
                                                this.project.extra.foundationType,
                                                Validators.required
                                            ),
                                            description: new FormControl(this.project.extra.description),
                                            execution_date: new FormControl(this.project.extra.execution_date),
                                            worknumber: new FormControl(this.project.extra.worknumber),
                                            street: new FormControl(this.project.extra.street),
                                            customer: new FormControl(
                                                this.project.extra.customer
                                                    ? this.project.extra.customer
                                                    : this.customer?.name
                                            ),
                                        };
                                        this.form = new FormGroup(this.fc);
                                        if (
                                            !AuthorisationService.hasAnyFunctionality([
                                                Functionalities.GeoManage,
                                                Functionalities.CustomerProjectsManage,
                                            ])
                                        ) {
                                            this.form.disable();
                                        }
                                    } else {
                                        this.project = project;
                                        if (phase) {
                                            this.selectedPhase = this.project.customer_project_phases.find(
                                                p => p.id === +phase
                                            );
                                            if (phaseActivity) {
                                                this.selectedActivity =
                                                    this.selectedPhase.customer_project_phase_activities.find(
                                                        a => a.id === +phaseActivity
                                                    );
                                            }
                                            if (reply) {
                                                this.highlightActivityReplyId = +reply;
                                            }
                                        }
                                    }
                                },
                                error =>
                                    this.confirmDialog
                                        .confirm('Fout bij het laden', error.message, 'Terug')
                                        .then(() => this.backButtonService.navigateBack())
                            );
                    })
                );
            }
        );
    }

    private getPageTitleLabel(): string {
        return this.Utils.isDrillingRoute(this.route) ? 'Booropdracht' : 'Project';
    }

    zipExport() {
        this.zipExporting = true;
        this.customerProjectService.zip(this.project.id).subscribe(zip => {
            const date = formatDate(new Date(), 'dd-MMM-yyyy', 'nl').replace('.', '');
            saveAs(zip, `Documenten ${this.project.name} ${date}.zip`);
            this.zipExporting = false;
        });
    }

    projectDescription() {
        this.dialog.open(CustomerProjectDescriptionDialogComponent, {
            panelClass: 'confirm-dialog',
            disableClose: true,
            data: {
                project: this.project,
            },
        });
    }

    editProject() {
        this.router.navigate([
            this.Routenames.customers,
            this.customerId,
            this.project.type === 'drillings' ? this.Routenames.drillingProjects : this.Routenames.projects,
            this.projectId,
            this.Routenames.edit,
        ]);
    }

    sidebarToggle() {
        this.hideSidebar = !this.hideSidebar;
    }

    save() {
        Utils.triggerValidationP(this.form)
            .then(() => {
                Object.assign(this.project.extra, this.form.value);
                this.subscriptions.add(
                    this.customerProjectService.saveProject(this.project).subscribe({
                        next: () => this.confirmDialog.confirm('Opgeslagen', 'De booropdracht is opgeslagen.', 'Oke'),
                        error: error =>
                            this.confirmDialog.confirm(
                                'Opslaan mislukt',
                                error?.message || 'Er is iets fout gegaan op de server.',
                                'Oke'
                            ),
                    })
                );
            })
            .catch(() => {});
    }

    deleteReport(filename: string) {
        this.confirmDialog
            .confirm('Verwijderen', 'Weet je zeker dat je dit document wilt verwijderen?', 'Verwijderen', 'Behouden')
            .then(() =>
                this.fileService.delete(`${this.project.id}/${filename}`, StorageName.drillings).subscribe(() => {
                    this.project.extra.reports.splice(this.project.extra.reports.indexOf(filename), 1);
                    this.customerProjectService.saveProject(this.project).subscribe();
                })
            )
            .catch(() => {});
    }

    fileUploaded(file: FileItem) {
        if (!this.project.extra.reports) {
            this.project.extra.reports = [];
        }
        this.project.extra.reports.push(file.filename);
        this.customerProjectService.saveProject(this.project).subscribe();
    }

    drop(event: CdkDragDrop<string[]>, items: any[]) {
        moveItemInArray(items, event.previousIndex, event.currentIndex);
    }

    exportExcel(data: PdfReportRequest) {
        this.generatingExcel = true;
        const statusIds = data.status
            ? Object.keys(data.status)
                  .filter(key => data.status[key])
                  .map(Number)
            : undefined;
        this.exportService.downloadExcelWorkareas(this.project.id, data.workarea_ids, statusIds).subscribe({
            next: () => {
                this.generatingExcel = false;
            },
            error: error => {
                this.generatingExcel = false;
                this.confirmDialog.confirm(
                    'Oeps...',
                    `Er is iets fout gegaan bij het maken van de Excel. ${error?.message}`,
                    'Oke'
                );
            },
        });
    }

    downloadReport(data: PdfReportRequest) {
        this.generatingReport = true;
        this.subscriptions.add(
            this.pdfreportService
                .getReportPdf(data)
                .pipe(map(document => ({ document, data })))
                .subscribe({
                    next: ({ document, data }) => {
                        this.generatingReport = false;
                        saveAs(
                            document,
                            `${this.project.type === 'drillings' ? 'Boorrapport' : 'Rapport'} ${data.pdf_style_name} ${this.customer.name} - ${this.project.name}.pdf`
                        );
                    },
                    error: error => {
                        this.generatingReport = false;
                        const blob: Blob = error.error;
                        if (blob instanceof Blob) {
                            blob.text().then(text => {
                                const parsed = JSON.parse(text);
                                this.confirmDialog.confirm(
                                    'Fout bij het genereren van het rapport',
                                    parsed?.message ?? '',
                                    'Oke'
                                );
                            });
                        } else {
                            this.confirmDialog.confirm(
                                'Fout bij het genereren van het rapport',
                                error?.message ?? '',
                                'Oke'
                            );
                        }
                    },
                })
        );
    }

    openExportDialog(type: '' | 'Pdf' | 'Excel') {
        const dialogRef = this.dialog.open(CustomerProjectExportDialogComponent, {
            maxHeight: '98vh',
            minWidth: '750px',
            panelClass: 'confirm-dialog',
            disableClose: true,
            data: {
                project: this.project,
                customer: this.customer,
                type,
            },
        });

        this.subscriptions.add(
            dialogRef
                .afterClosed()
                .pipe(filter(result => !!result))
                .subscribe(result => {
                    if (type === 'Pdf') {
                        this.downloadReport(result);
                    } else if (type === 'Excel') {
                        this.exportExcel(result);
                    }
                })
        );
    }
}
